import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Button, 
  Typography, 
  Box, 
  Alert, 
  Snackbar, 
  Paper, 
  CircularProgress,
  useTheme,
  Fade,
  Zoom,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UpdateIcon from '@mui/icons-material/Update';
import ProjectCard from '../components/ProjectCard';
import ProjectForm from '../components/CreateProjectForm';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

// Importando as bibliotecas do @dnd-kit
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente sortable para o card de projeto
const SortableProjectItem = ({ project, isAdmin, activeId, overIndex, currentIndex }) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.project_id.toString() });

  // Determinar animação com base em se está sendo arrastado sobre
  let animateTransform = transform;
  const isCurrent = activeId && activeId === project.project_id.toString();
  const isOver = !isCurrent && activeId && currentIndex === overIndex;
  
  // Se este card não está sendo arrastado, mas outro card está sobre ele
  if (isOver) {
    // Verificar a posição e definir deslocamento lateral
    const direction = currentIndex > overIndex ? -1 : 1;
    const offset = direction * 20; // deslocamento lateral em pixels
    
    // Criar um transform modificado para o deslocamento lateral
    animateTransform = {
      ...transform,
      x: (transform?.x || 0) + offset,
    };
  }

  const style = {
    transform: CSS.Transform.toString(animateTransform),
    transition: transition || "transform 250ms cubic-bezier(0.2, 1, 0.1, 1)",
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 9999 : isOver ? 40 : 'auto',
    position: 'relative',
  };

  // Combinar atributos e listeners para o drag handle
  const dragHandleProps = isAdmin ? { ...attributes, ...listeners } : null;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        ref={setNodeRef}
        style={style}
      >
        <Box 
          position="relative"
          sx={{
            border: isDragging ? `2px dashed ${theme.palette.primary.main}` : isOver ? `2px solid ${theme.palette.primary.light}` : 'none',
            borderRadius: '8px',
            height: '100%',
            transition: 'all 0.3s ease',
            mt: '5px',
            minHeight: '350px', // Altura mínima fixa para todos os cards
            backgroundColor: isDragging 
              ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(232, 244, 253, 0.6)' 
              : isOver 
                ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(232, 244, 253, 0.8)'
                : 'transparent',
            '&:hover': {
              transform: isAdmin ? 'translateY(-4px)' : 'none',
              boxShadow: theme.shadows[8]
            },
            boxShadow: isOver ? theme.shadows[4] : 'none',
            transform: isOver ? 'scale(1.02)' : 'none',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Bandeirinha com número do ranking */}
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2
            }}
            style={{
              position: 'absolute',
              top: 15, // Aumentado para descer a bandeirinha
              right: 15,
              zIndex: 10,
              transformOrigin: 'top center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '35px',
                height: '50px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transform: 'none',
              }}
            >
              {/* Parte superior da bandeirinha */}
              <Box
                sx={{
                  width: '35px', // Ajustado para manter proporção
                  height: '35px', // Ajustado para manter proporção
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '4px 4px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.1rem', // Reduzido um pouco mais a fonte
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                }}
              >
                {project.displayOrder || project.priority_order}
              </Box>
              {/* Parte inferior triangular */}
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: '17.5px solid transparent', // Ajustado para manter proporção
                  borderRight: '17.5px solid transparent', // Ajustado para manter proporção
                  borderTop: `15px solid ${theme.palette.primary.main}`,
                  filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))',
                }}
              />
            </Box>
          </motion.div>

          <Box sx={{ 
            height: '100%', 
            p: 2,
            pt: 4, // Aumentado o padding superior para acomodar melhor a bandeirinha
            display: 'flex',
            flexDirection: 'column'
          }}>
            <ProjectCard 
              project={{
                id: project.project_id,
                project_id: project.project_id,
                name: project.project_name,
                description: project.description,
                priority: project.priority,
                responsible: project.responsible,
                startDate: project.start_date,
                endDate: project.end_date,
                status_id: project.status_id
              }} 
              dragHandleProps={dragHandleProps}
              isAdmin={isAdmin}
            />
          </Box>
        </Box>
      </motion.div>
    </Grid>
  );
};

// Componente para a overlay durante o drag
const DragOverlayContent = ({ project, isAdmin }) => {
  const theme = useTheme();
  
  // Combinar atributos e listeners para o drag handle
  const dragHandleProps = isAdmin ? {} : null;

  return (
    <Box 
      sx={{ 
        width: '100%', 
        maxWidth: '350px',
        height: '350px', // Altura fixa consistente com os outros cards
        transform: 'scale(1.02)',
        opacity: 0.9,
        boxShadow: theme.shadows[10],
        borderRadius: '8px',
        pointerEvents: 'none',
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${theme.palette.primary.main}`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <ProjectCard 
        project={{
          id: project.project_id,
          project_id: project.project_id,
          name: project.project_name,
          description: project.description,
          priority: project.priority,
          responsible: project.responsible,
          startDate: project.start_date,
          endDate: project.end_date,
          status_id: project.status_id
        }} 
        dragHandleProps={dragHandleProps}
        isAdmin={isAdmin}
      />
    </Box>
  );
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [tabValue, setTabValue] = useState(0); // 0 = Em andamento, 1 = Concluídos
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = useTheme();
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Configuração dos sensores para o dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Distância mínima para iniciar o arrastar (em pixels)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Função para filtragem de projetos com base na aba selecionada
  const filterProjects = () => {
    // Se não há projetos, retorna array vazio
    if (!projects || projects.length === 0) {
      setFilteredProjects([]);
      return;
    }

    // Filtra projetos baseado na aba selecionada
    const filtered = projects.filter(project => {
      // Aba 0 = Em andamento (status_id === 1)
      // Aba 1 = Concluídos (status_id === 2)
      return tabValue === 0 ? project.status_id === 1 : project.status_id === 2;
    });

    // Ordena os projetos por priority_order
    const sorted = [...filtered].sort((a, b) => a.priority_order - b.priority_order);
    
    // Se estamos na aba de projetos em andamento, recalcular a ordem visível
    // para mostrar sequencialmente como 1, 2, 3...
    if (tabValue === 0) {
      const reordered = sorted.map((project, index) => ({
        ...project,
        displayOrder: index + 1 // Ordem de exibição começa em 1
      }));
      setFilteredProjects(reordered);
    } else {
      // Para projetos concluídos, mantemos a ordem de prioridade original
      const withDisplayOrder = sorted.map(project => ({
        ...project,
        displayOrder: project.priority_order
      }));
      setFilteredProjects(withDisplayOrder);
    }
  };

  // Atualiza os projetos filtrados quando projetos ou aba mudar
  useEffect(() => {
    filterProjects();
  }, [projects, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return false;
    }
    return token;
  };

  const checkUserRole = async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        // Verificar se é admin ou técnico
        const isAdminUser = response.data.role_id === 1;
        const isTechnicalUser = response.data.role_id === 3;
        setIsAdmin(isAdminUser || isTechnicalUser);
        
        // Armazenar a role para acesso em outros componentes
        localStorage.setItem('role', response.data.role_id);
      }
    } catch (error) {
      console.error('Erro ao verificar permissões do usuário:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = checkAuth();
      if (!token) return;

      const response = await api.get('/projects/', {
        params: { sort_by: 'priority_order' }
      });
      
      // Verificar resposta da API
      console.log('Projetos recebidos:', response.data);
      
      setProjects(response.data);
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setNotification({
        open: true,
        message: 'Erro ao carregar projetos: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    checkUserRole();
  }, [navigate]);

  // Log de montagem do componente
  useEffect(() => {
    console.log('Componente Projects montado');
    return () => console.log('Componente Projects desmontado');
  }, []);

  // Log quando os projetos são atualizados
  useEffect(() => {
    console.log('Estado de projetos atualizado:', projects);
  }, [projects]);

  const handleCreateProject = async (formData) => {
    try {
      const token = checkAuth();
      if (!token) return;

      // Format data for API
      const newProject = {
        project_name: formData.name,
        description: formData.description,
        priority: formData.priority,
        responsible: formData.responsible,
        start_date: formData.startDate,
        end_date: formData.endDate,
        priority_order: projects.length + 1 // Adicionar ao final da lista
      };

      await api.post('/projects/', newProject);

      setOpenCreateForm(false);
      setNotification({
        open: true,
        message: 'Projeto criado com sucesso!',
        severity: 'success'
      });

      // Refresh projects
      fetchProjects();
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setNotification({
        open: true,
        message: 'Erro ao criar projeto: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    }
  };

  // Encontrar projeto ativo durante o drag
  const activeProject = activeId ? filteredProjects.find(project => 
    project.project_id.toString() === activeId
  ) : null;
  
  // Encontrar índices para animação
  const activeIndex = activeId 
    ? filteredProjects.findIndex(project => project.project_id.toString() === activeId)
    : -1;
  
  const overIndex = overId
    ? filteredProjects.findIndex(project => project.project_id.toString() === overId)
    : -1;

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };
  
  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      setOverId(over.id);
    }
  };

  const handleDragEnd = (event) => {
    console.log('handleDragEnd called with event:', event);
    
    const { active, over } = event;
    
    // Resetar estados
    setActiveId(null);
    setOverId(null);
    
    // Se não houve mudança, retornar
    if (!active || !over || active.id === over.id) {
      return;
    }
    
    console.log(`Moving item from ${active.id} to ${over.id}`);
    
    // Encontrar os índices dos itens
    const oldIndex = filteredProjects.findIndex(project => project.project_id.toString() === active.id);
    const newIndex = filteredProjects.findIndex(project => project.project_id.toString() === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      // Reordenar projetos na lista filtrada
      const updatedFilteredProjects = arrayMove(filteredProjects, oldIndex, newIndex);
      
      // Separar os projetos por status
      const activeProjects = projects.filter(p => p.status_id === 1);
      const completedProjects = projects.filter(p => p.status_id === 2);
      
      // Se estamos na aba de projetos em andamento, atualizar apenas a ordem dos projetos ativos
      if (tabValue === 0) {
        // Atualizar os valores de priority_order para os projetos em andamento
        const activeProjectsWithUpdatedOrder = updatedFilteredProjects.map((project, index) => ({
          ...project,
          priority_order: index + 1, // Começar com 1
          displayOrder: index + 1
        }));
        
        // Combinar com projetos concluídos (que mantêm suas ordens)
        const allProjectsUpdated = [
          ...activeProjectsWithUpdatedOrder,
          ...completedProjects
        ];
        
        // Atualizar o estado
        setProjects(allProjectsUpdated);
        setOrderChanged(true);
      }
      // Caso contrário, estamos na aba de concluídos
      else {
        // Apenas atualizar a ordem de exibição para a UI, mas não alteramos a ordem real
        // já que não permitimos reordenar projetos concluídos
        const updatedWithDisplayOrder = updatedFilteredProjects.map((project, index) => ({
          ...project,
          displayOrder: project.priority_order // Manter a ordem original
        }));
        
        setFilteredProjects(updatedWithDisplayOrder);
      }
    }
  };

  const handleSaveOrder = async () => {
    try {
      // Verificar permissões
      if (!isAdmin) {
        setNotification({
          open: true,
          message: 'Apenas administradores podem alterar a ordem dos projetos',
          severity: 'error'
        });
        return;
      }

      setSavingOrder(true);
      
      // Preparar os dados para enviar ao backend
      // Enviamos apenas os projetos em andamento com suas novas ordens
      const activeProjects = projects.filter(p => p.status_id === 1);
      
      // Ordenar projetos ativos pela priority_order atual
      const sortedActive = [...activeProjects].sort((a, b) => a.priority_order - b.priority_order);
      
      // Preparar o payload com as ordens atualizadas
      const projectOrders = sortedActive.map((project, index) => ({
        project_id: project.project_id,
        priority_order: index + 1 // Garantir sequência consecutiva 1, 2, 3...
      }));
      
      // Enviar para o backend
      await api.put('/projects/update-order/', projectOrders);
      
      setNotification({
        open: true,
        message: 'Ordem dos projetos atualizada com sucesso!',
        severity: 'success'
      });
      
      setOrderChanged(false);
      
      // Atualizar a lista de projetos
      fetchProjects();
    } catch (error) {
      console.error('Erro ao salvar ordem dos projetos:', error);
      setNotification({
        open: true,
        message: 'Erro ao salvar ordem dos projetos: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const handleRefresh = () => {
    fetchProjects();
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="textSecondary">
            Carregando projetos...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          mt: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{
              fontWeight: 600,
              background: theme => theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #90caf9 30%, #64b5f6 90%)'
                : 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              pb: 1,
              borderBottom: theme => `2px solid ${theme.palette.primary.main}`,
              display: 'inline-block'
            }}
          >
            Projetos
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Atualizar lista" arrow>
              <IconButton
                color="primary"
                onClick={handleRefresh}
                size="small"
                sx={{
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : 'rgba(25, 118, 210, 0.12)'
                  }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {isAdmin && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenCreateForm(true)}
                  startIcon={<AddIcon fontSize="small" />}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    py: 0.8,
                    px: 1.5,
                    '&:hover': {
                      boxShadow: theme => theme.shadows[4]
                    }
                  }}
                >
                  Criar Novo Projeto
                </Button>
                
                {orderChanged && (
                  <Zoom in={orderChanged}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon fontSize="small" />}
                      onClick={handleSaveOrder}
                      disabled={savingOrder}
                      size="small"
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        py: 0.8,
                        px: 1.5,
                        '&:hover': {
                          boxShadow: theme => theme.shadows[4]
                        }
                      }}
                    >
                      {savingOrder ? 'Salvando...' : 'Salvar Ordem'}
                    </Button>
                  </Zoom>
                )}
              </>
            )}
          </Box>
        </Box>
        
        {/* Abas para alternar entre projetos em andamento e concluídos */}
        <Paper 
          sx={{ 
            mb: 3, 
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: theme.shadows[1],
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 48,
                transition: 'all 0.3s',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }
            }}
          >
            <Tab 
              icon={<UpdateIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />}
              iconPosition="start"
              label={`Em Andamento (${projects.filter(p => p.status_id === 1).length})`} 
              sx={{ py: 1.5 }} 
            />
            <Tab 
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />}
              iconPosition="start"
              label={`Concluídos (${projects.filter(p => p.status_id === 2).length})`} 
              sx={{ py: 1.5 }} 
            />
          </Tabs>
        </Paper>

        {isAdmin && tabValue === 0 && (
          <Fade in>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                mb: 4,
                backgroundColor: theme => theme.palette.mode === 'dark' 
                  ? 'rgba(144, 202, 249, 0.12)' 
                  : 'rgba(25, 118, 210, 0.12)',
                borderRadius: '8px',
                border: theme => `1px solid ${theme.palette.divider}`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                <DragIndicatorIcon 
                  fontSize="large" 
                  color="primary" 
                  sx={{ 
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.6, transform: 'scale(1)' },
                      '50%': { opacity: 1, transform: 'scale(1.1)' },
                      '100%': { opacity: 0.6, transform: 'scale(1)' }
                    }
                  }} 
                />
                
                <Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    Arraste para reordenar projetos
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    Use o ícone <DragIndicatorIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> 
                    para arrastar e soltar os projetos, alterando sua ordem de prioridade.
                    O projeto na posição #1 será considerado o mais importante.
                  </Typography>
                </Box>
              </Box>
              
              {orderChanged && (
                <Zoom in={orderChanged}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon fontSize="small" />}
                    onClick={handleSaveOrder}
                    disabled={savingOrder}
                    size="small"
                    sx={{
                      position: { xs: 'relative', sm: 'absolute' },
                      right: { xs: 'auto', sm: 20 },
                      top: { xs: 'auto', sm: '50%' },
                      mt: { xs: 2, sm: 0 },
                      transform: { xs: 'none', sm: 'translateY(-50%)' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      py: 0.8,
                      px: 1.5,
                      '&:hover': {
                        boxShadow: theme => theme.shadows[4]
                      }
                    }}
                  >
                    {savingOrder ? 'Salvando...' : 'Salvar Ordem'}
                  </Button>
                </Zoom>
              )}
            </Paper>
          </Fade>
        )}

        <AnimatePresence>
          {filteredProjects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: '8px',
                  backgroundColor: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.08)' 
                    : 'rgba(25, 118, 210, 0.08)',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {tabValue === 0 ? "Nenhum projeto em andamento" : "Nenhum projeto concluído"}
                </Typography>
                <Typography variant="body1" color="textSecondary" mb={3}>
                  {tabValue === 0 
                    ? (isAdmin ? 'Clique em "Criar Novo Projeto" para adicionar seu primeiro projeto' : 'Não há projetos em andamento no momento')
                    : 'Não há projetos concluídos no momento'}
                </Typography>
                {isAdmin && tabValue === 0 && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => setOpenCreateForm(true)}
                    startIcon={<AddIcon fontSize="small" />}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      py: 0.8,
                      px: 1.5,
                      '&:hover': {
                        boxShadow: theme => theme.shadows[4]
                      }
                    }}
                  >
                    Criar Novo Projeto
                  </Button>
                )}
              </Paper>
            </motion.div>
          ) : (
            // Usar o DndContext apenas na aba de projetos em andamento
            tabValue === 0 ? (
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={filteredProjects.map(project => project.project_id.toString())}
                  strategy={rectSortingStrategy}
                >
                  <Grid container spacing={3}>
                    {filteredProjects.map((project, index) => (
                      <SortableProjectItem 
                        key={project.project_id} 
                        project={project} 
                        isAdmin={isAdmin && tabValue === 0} // Permite arrastar apenas na aba "Em andamento"
                        activeId={activeId}
                        overIndex={overIndex}
                        currentIndex={index}
                      />
                    ))}
                  </Grid>
                </SortableContext>
                
                {/* Adicionar DragOverlay para visualizar o card durante o drag */}
                <DragOverlay adjustScale={true} zIndex={1000}>
                  {activeId && activeProject ? (
                    <DragOverlayContent 
                      project={activeProject} 
                      isAdmin={isAdmin}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            ) : (
              // Renderização simples para a aba de projetos concluídos (sem drag and drop)
              <Grid container spacing={3}>
                {filteredProjects.map((project, index) => (
                  <SortableProjectItem 
                    key={project.project_id} 
                    project={project} 
                    isAdmin={false} // Desabilita o arraste na aba de concluídos
                    activeId={null}
                    overIndex={-1}
                    currentIndex={index}
                  />
                ))}
              </Grid>
            )
          )}
        </AnimatePresence>

        <ProjectForm
          open={openCreateForm}
          onClose={() => setOpenCreateForm(false)}
          onSubmit={handleCreateProject}
          isEditing={false}
        />

        <Snackbar 
          open={notification.open} 
          autoHideDuration={6000} 
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          TransitionComponent={Fade}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            sx={{ 
              width: '100%',
              borderRadius: '8px',
              boxShadow: theme => theme.shadows[3]
            }}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default Projects; 